import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { applyTenant, STATUS_META, PRIORITY_META, fmtDate, initials, resolveAsset } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Globe, Mail, BarChart3, UserPlus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [userOpen, setUserOpen] = useState(false);
  const [nu, setNu] = useState({ name: "", email: "", password: "" });

  const load = async () => {
    const [c, u, t, m] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get("/users"),
      api.get(`/tickets?client_id=${id}`),
      api.get(`/milestones?client_id=${id}`),
    ]);
    setClient(c.data);
    applyTenant(c.data.primary_color, c.data.accent_color);
    setEdit(c.data);
    setUsers(u.data.filter((x) => x.client_id === id));
    setTickets(t.data);
    setMilestones(m.data);
  };
  useEffect(() => { load(); return () => applyTenant("#F7941E", "#0E4C7A"); }, [id]);

  const saveEdit = async () => {
    try {
      const { data } = await api.patch(`/clients/${id}`, {
        name: edit.name, domain: edit.domain, contact_name: edit.contact_name,
        contact_email: edit.contact_email, primary_color: edit.primary_color,
        accent_color: edit.accent_color, plan: edit.plan,
        ga4_property_id: edit.ga4_property_id, gsc_site_url: edit.gsc_site_url,
        industry: edit.industry, location: edit.location,
        meta_ad_account_id: edit.meta_ad_account_id, google_ads_customer_id: edit.google_ads_customer_id,
      });
      setClient(data);
      applyTenant(data.primary_color, data.accent_color);
      setEditOpen(false);
      toast.success("Client updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addUser = async () => {
    if (!nu.email || !nu.password) return toast.error("Email and password required");
    try {
      await api.post("/users", { ...nu, role: "client", client_id: id });
      toast.success("Login created");
      setUserOpen(false);
      setNu({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeClient = async () => {
    await api.delete(`/clients/${id}`);
    toast.success("Client deleted");
    navigate("/clients");
  };

  if (!client) return <div className="h-40 rounded-md bg-white border border-slate-200 animate-pulse" />;

  return (
    <div className="animate-fade-up space-y-6">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-clients-btn">
        <ArrowLeft size={15} /> Back to clients
      </button>

      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4 flex-wrap">
          {client.logo_url ? (
            <img src={resolveAsset(client.logo_url)} alt="" className="h-16 w-16 rounded object-contain bg-slate-50 p-1.5 border border-slate-100" />
          ) : (
            <div className="h-16 w-16 rounded flex items-center justify-center text-white font-display text-2xl font-bold" style={{ background: client.primary_color }}>{client.name[0]}</div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-bold text-slate-900">{client.name}</h1>
            <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500 flex-wrap">
              <span className="flex items-center gap-1.5"><Globe size={14} /> {client.domain || "—"}</span>
              <span className="flex items-center gap-1.5"><Mail size={14} /> {client.contact_email || "—"}</span>
              <span className="text-[11px] px-2 py-0.5 rounded" style={{ background: `${client.primary_color}1a`, color: client.primary_color }}>{client.plan}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/seo")} data-testid="client-seo-btn"><BarChart3 size={15} className="mr-1.5" /> SEO Report</Button>
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="edit-client-btn"><Pencil size={15} /></Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Edit client</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1.5" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Domain</Label><Input value={edit.domain || ""} onChange={(e) => setEdit({ ...edit, domain: e.target.value })} className="mt-1.5" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Contact name</Label><Input value={edit.contact_name || ""} onChange={(e) => setEdit({ ...edit, contact_name: e.target.value })} className="mt-1.5" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Contact email</Label><Input value={edit.contact_email || ""} onChange={(e) => setEdit({ ...edit, contact_email: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Brand</Label><input type="color" value={edit.primary_color || "#0EA5E9"} onChange={(e) => setEdit({ ...edit, primary_color: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Accent</Label><input type="color" value={edit.accent_color || "#3B82F6"} onChange={(e) => setEdit({ ...edit, accent_color: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Plan</Label><Input value={edit.plan || ""} onChange={(e) => setEdit({ ...edit, plan: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Industry</Label><Input value={edit.industry || ""} onChange={(e) => setEdit({ ...edit, industry: e.target.value })} placeholder="e.g. Personal injury law firm" className="mt-1.5" data-testid="edit-industry-input" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Location</Label><Input value={edit.location || ""} onChange={(e) => setEdit({ ...edit, location: e.target.value })} placeholder="e.g. Atlanta, GA" className="mt-1.5" data-testid="edit-location-input" /></div>
                  </div>
                  <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-2">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Analytics & Ads Connectors</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Input value={edit.ga4_property_id || ""} onChange={(e) => setEdit({ ...edit, ga4_property_id: e.target.value })} placeholder="GA4 Property ID" data-testid="edit-ga4-input" />
                      <Input value={edit.gsc_site_url || ""} onChange={(e) => setEdit({ ...edit, gsc_site_url: e.target.value })} placeholder="GSC site URL (https://…)" data-testid="edit-gsc-input" />
                      <Input value={edit.meta_ad_account_id || ""} onChange={(e) => setEdit({ ...edit, meta_ad_account_id: e.target.value })} placeholder="Meta Ad Account ID" data-testid="edit-meta-input" />
                      <Input value={edit.google_ads_customer_id || ""} onChange={(e) => setEdit({ ...edit, google_ads_customer_id: e.target.value })} placeholder="Google Ads Customer ID" data-testid="edit-gads-input" />
                    </div>
                    <p className="text-[11px] text-slate-400">Grant the CloudPoweredIT service account read access, then enter IDs to pull live analytics.</p>
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={removeClient} className="text-red-600 border-red-200 hover:bg-red-50 mr-auto" data-testid="delete-client-btn"><Trash2 size={15} className="mr-1.5" /> Delete</Button>
                  <Button onClick={saveEdit} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="save-client-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="tickets">
        <TabsList>
          <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets ({tickets.length})</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones ({milestones.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="mt-4">
          <Card className="border-slate-200 divide-y">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left" data-testid="cd-ticket-row">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: STATUS_META[t.status].dot }} />
                <span className="text-sm text-slate-800 flex-1 truncate">{t.title}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded ${PRIORITY_META[t.priority].bg}`}>{PRIORITY_META[t.priority].label}</span>
              </button>
            ))}
            {tickets.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No tickets.</p>}
          </Card>
        </TabsContent>

        <TabsContent value="milestones" className="mt-4 space-y-3">
          {milestones.map((m) => (
            <Card key={m.id} className="p-4 border-slate-200 flex items-center justify-between">
              <div><p className="text-sm font-medium text-slate-800">{m.title}</p><p className="text-xs text-slate-400">Due {fmtDate(m.due_date)} · {m.progress}%</p></div>
              <div className="w-28 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full tenant-accent-bg" style={{ width: `${m.progress}%` }} /></div>
            </Card>
          ))}
          {milestones.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No milestones.</p>}
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogTrigger asChild><Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="add-user-btn"><UserPlus size={15} className="mr-1.5" /> Add login</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Add client login</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Display name</Label><Input value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} className="mt-1.5" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Username (email)</Label><Input value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} className="mt-1.5" data-testid="new-user-email" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Password</Label><Input value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} className="mt-1.5" data-testid="new-user-password" /></div>
                </div>
                <DialogFooter><Button onClick={addUser} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="submit-user-btn">Create login</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="border-slate-200 divide-y">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid="user-row">
                <Avatar className="h-9 w-9"><AvatarFallback className="text-white text-xs" style={{ background: "var(--tenant-primary)" }}>{initials(u.name)}</AvatarFallback></Avatar>
                <div><p className="text-sm font-medium text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></div>
                <span className="ml-auto text-[11px] uppercase tracking-wider text-slate-400">{u.role}</span>
              </div>
            ))}
            {users.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No client logins yet.</p>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
